/**
 * Amazon tracking screen (M4) — /app/amazon, visible only when the 'amazon'
 * module is enabled for the tenant. Read-only snapshot of SP-API inventory
 * plus the last sync run; sync is an explicit user click (hard rule: no
 * automated Amazon writes — this screen only reads).
 *
 * Inventory table (UI redesign, docs/plans/2026-08-24-ui-redesign-sunlit-
 * cockpit.md): a Seller-Central-style density table, full-width (max-w-6xl,
 * like PlanningPage) with grouped column headers — PRODUCT | INVENTORY
 * (fulfillable, inbound breakdown, reserved) | PERFORMANCE (live sales
 * velocity, Make Today real phase 2 Instrument 3, 2026-09-01) | PRICE &
 * FEES (live Your price / Buy box / fees, same instrument). Toolbar has a
 * client-side search (SKU/ASIN/name) and state filter pills (All/Selling/
 * Not selling — "selling" is still a fulfillable>0 stand-in; it does not
 * read the Performance column's real sales data). Columns sort
 * client-side; pure helpers live in lib/amazon.ts. InventoryRow/
 * InventoryTable are exported for direct renderToStaticMarkup tests (see
 * AmazonPage.test.tsx), matching PriceCalibrationRow.tsx's convention.
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from 'lucide-react';
import { Badge, Button, cn, Card, Input } from '../../design';
import {
  AMAZON_STATE_FILTER_PILLS,
  amazonStateFilterCounts,
  buyboxDisplayLabel,
  cogsAtCostLabel,
  cogsBreakdownTitle,
  cogsPrimaryLabel,
  DEFAULT_AMAZON_STATE_FILTER,
  feesCheckedAtLabel,
  feesDisplayLabel,
  filterAmazonInventoryByState,
  isOutOfStock,
  sortUnitFlowDisplayRows,
  performancePrimaryLabel,
  performanceSecondaryLabel,
  searchAmazonInventory,
  sortAmazonInventory,
  unitFlowCoverageDisplay,
  unitFlowRowsToDisplay,
  useAmazonState,
  useAmazonUnitFlow,
  useAmazonSync,
  useSetAmazonListingPrepWrap,
  type AmazonSortDirection,
  type AmazonSortKey,
  type AmazonStateFilterKey,
  UNIT_FLOW_HEADER_CLASS,
  UNIT_FLOW_ROW_CLASS,
} from '../../lib/amazon';
import { amazonRetailReasonsTitle, money } from '../../lib/order-planning';
import { useModuleEnabled } from '../../lib/auth';
import { ApiCallError } from '../../lib/api';
import { AppShell } from '../../components/AppShell';
import { ProductThumb } from '../../components/ProductThumb';
import { ProductLink } from '../../components/ProductLink';
import { SkeletonLine, SkeletonRows } from '../../components/Skeleton';
import { MathCard } from './MathCard';
import type { AmazonInventoryRow, AmazonSyncRun, AmazonUnitFlowResponse } from '@sourcito/schemas';

const RUN_STATUS_LABEL: Record<AmazonSyncRun['status'], string> = {
  running: 'syncing…',
  ok: 'ok',
  error: 'error',
};

export function AmazonPage() {
  const enabled = useModuleEnabled('amazon');
  const { data, isLoading, isError } = useAmazonState(enabled);
  const unitFlow = useAmazonUnitFlow(enabled);

  if (!enabled) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              The Amazon module isn't enabled for this workspace.{' '}
              <Link to="/app/brands" className="text-accent-fg hover:underline">
                Back to Brands
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  const inventory = data?.inventory ?? [];

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-medium leading-tight">Amazon Inventory</h1>
              <p className="text-xs text-fg-tertiary">
                {inventory.length > 0
                  ? `${inventory.length} SKU${inventory.length === 1 ? '' : 's'}`
                  : ' '}
              </p>
            </div>
            <SyncSection lastRun={data?.lastRun ?? null} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8">
        {isLoading && <InventoryTableSkeleton />}
        {isError && <p className="text-sm text-negative">Could not load Amazon inventory.</p>}
        {!isLoading && !isError && inventory.length === 0 && (
          <Card>
            <p className="text-sm text-fg-secondary">
              No inventory synced yet — run your first sync.
            </p>
          </Card>
        )}
        {!isLoading && !isError && inventory.length > 0 && <InventoryTable rows={inventory} />}
        {/* Unit flow is a reconciliation audit, not a daily operating
            surface — it lives below the inventory table (Faraz 2026-09-01). */}
        <UnitFlowSection
          data={unitFlow.data}
          isLoading={unitFlow.isLoading}
          isError={unitFlow.isError}
        />
      </main>
    </AppShell>
  );
}

function UnitFlowSection({
  data,
  isLoading,
  isError,
}: {
  data: AmazonUnitFlowResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const [expandedSku, setExpandedSku] = useState<string | null>(null);
  const [showReconciled, setShowReconciled] = useState(false);
  const rows = data?.rows ?? [];
  const displayRows = useMemo(
    () => sortUnitFlowDisplayRows(unitFlowRowsToDisplay(rows, data?.coverage)),
    [rows, data?.coverage]
  );
  // Attention rows (non-zero residual on either leg) always render, tinted;
  // fully reconciled rows collapse behind a summary toggle so the panel is
  // small when everything balances (Faraz 2026-09-01).
  const attentionRows = displayRows.filter((r) => r.needsAttention);
  const reconciledRows = displayRows.filter((r) => !r.needsAttention);
  const visibleFlowRows = showReconciled ? displayRows : attentionRows;
  const coverage = unitFlowCoverageDisplay(data?.coverage);

  return (
    <section aria-labelledby="unit-flow-heading" className="mb-10">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <h2 id="unit-flow-heading" className="text-lg font-medium">
            Unit flow
          </h2>
          <p className="text-xs text-fg-tertiary mt-0.5">{coverage.subtitle}</p>
        </div>
        {data?.coverage?.complete && data.residualCents !== null && (
          <p className="text-xs text-fg-secondary">
            Total exposure: <span className="font-medium">{moneyEstimate(data.residualCents)}</span>
          </p>
        )}
      </div>

      {isLoading && <UnitFlowSectionSkeleton />}
      {isError && <p className="text-sm text-negative">Could not load unit flow.</p>}
      {!isLoading && !isError && data && (
        <p className={`text-xs ${coverage.toneClass} mb-3`}>{coverage.message}</p>
      )}
      {!isLoading && !isError && data?.coverage?.complete && data.uncostedResidualSkuCount > 0 && (
        <p className="text-xs text-warning mb-3">
          Incomplete cost data: {data.uncostedResidualSkuCount} SKU
          {data.uncostedResidualSkuCount === 1 ? '' : 's'} have nonzero residual units without a
          contributing lot cost. Dollar exposure is unavailable for those rows.
        </p>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <Card>
          <p className="text-sm text-fg-secondary">
            No unit-flow records yet — run an Amazon sync.
          </p>
        </Card>
      )}
      {!isLoading && !isError && rows.length > 0 && (
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          <div className={UNIT_FLOW_HEADER_CLASS}>
            <span>Product</span>
            <span>Warehouse expected on-hand</span>
            <span>Amazon inventory variance</span>
            <span>Dollar exposure</span>
          </div>
          <div className="divide-y divide-border-subtle">
            {visibleFlowRows.length === 0 && (
              <p className="text-sm text-fg-secondary px-4 py-4">
                All products reconcile — every unit is accounted for.
              </p>
            )}
            {visibleFlowRows.map((display) => {
              const expanded = expandedSku === display.sellerSku;
              const label = display.productName ?? display.sellerSku;
              const detailsId = `unit-flow-details-${display.sellerSku}`;
              return (
                <div
                  key={display.sellerSku}
                  className={display.needsAttention ? 'bg-warning/[0.06]' : undefined}
                >
                  <div className={`w-full text-left ${UNIT_FLOW_ROW_CLASS}`}>
                    <span className="min-w-0">
                      <span className="flex items-start gap-1 min-w-0">
                        <span className="min-w-0 truncate text-sm text-fg-primary">
                          <ProductLink asin={display.asin}>{label}</ProductLink>
                        </span>
                        <button
                          type="button"
                          className="shrink-0 text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} unit-flow details for ${label}`}
                          aria-expanded={expanded}
                          aria-controls={detailsId}
                          onClick={() => setExpandedSku(expanded ? null : display.sellerSku)}
                        >
                          {expanded ? (
                            <ChevronUp className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          )}
                        </button>
                      </span>
                      <span className="block text-xs font-mono text-fg-tertiary truncate">
                        {display.sellerSku} · {display.mappingLabel}
                      </span>
                    </span>
                    <span className="flex justify-between gap-3 text-sm tabular-nums text-fg-primary md:block">
                      <span className="text-xs text-fg-tertiary md:hidden">
                        Warehouse expected on-hand
                      </span>
                      {display.warehouseExpectedOnHand}
                    </span>
                    <span className="flex justify-between gap-3 text-sm tabular-nums text-fg-primary md:block">
                      <span className="text-xs text-fg-tertiary md:hidden">
                        Amazon inventory variance
                      </span>
                      {display.amazonResidual}
                    </span>
                    <span className="flex justify-between gap-3 text-sm tabular-nums text-fg-primary md:block">
                      <span className="text-xs text-fg-tertiary md:hidden">Dollar exposure</span>
                      {display.dollarExposure}
                    </span>
                  </div>
                  {expanded && (
                    <div id={detailsId} className="px-4 pb-4">
                      <MathCard data={display.cardData} variant="unit-flow" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {reconciledRows.length > 0 && (
            <button
              type="button"
              onClick={() => setShowReconciled((v) => !v)}
              className="w-full text-left text-xs text-fg-tertiary hover:text-fg-primary transition-colors duration-fast px-4 py-2.5 border-t border-border-subtle"
            >
              {showReconciled
                ? `Hide ${reconciledRows.length} reconciled product${reconciledRows.length === 1 ? '' : 's'}`
                : `${reconciledRows.length} product${reconciledRows.length === 1 ? '' : 's'} fully reconciled — show`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** Shimmer placeholder for UnitFlowSection's flow list while the query is
 *  loading — mirrors UNIT_FLOW_HEADER_CLASS/UNIT_FLOW_ROW_CLASS so the real
 *  rows don't jump the layout on load. */
function UnitFlowSectionSkeleton() {
  return (
    <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
      <div className={UNIT_FLOW_HEADER_CLASS}>
        <span>Product</span>
        <span>Warehouse expected on-hand</span>
        <span>Amazon inventory variance</span>
        <span>Dollar exposure</span>
      </div>
      <div className="divide-y divide-border-subtle">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className={UNIT_FLOW_ROW_CLASS} aria-hidden="true">
            <SkeletonLine className="w-40" />
            <SkeletonLine className="w-16 md:mx-auto" />
            <SkeletonLine className="w-16 md:mx-auto" />
            <SkeletonLine className="w-20 md:mx-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

function moneyEstimate(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} estimate`;
}

function SyncSection({ lastRun }: { lastRun: AmazonSyncRun | null }) {
  const sync = useAmazonSync();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<{ text: string; hint: boolean } | null>(null);

  async function handleSync() {
    setNotice(null);
    setError(null);
    try {
      const result = await sync.mutateAsync();
      setNotice(
        `Synced: ${result.inventoryCount} SKUs, ${result.checkinsApplied} check-ins applied, ${result.mappingsUpdated} mappings updated`
      );
    } catch (err) {
      if (err instanceof ApiCallError) {
        setError({ text: err.message, hint: err.status === 400 });
      } else {
        setError({ text: 'Sync failed.', hint: false });
      }
    }
  }

  return (
    <div className="text-right shrink-0 space-y-1">
      <Button size="sm" disabled={sync.isPending} loading={sync.isPending} onClick={handleSync}>
        Sync now
      </Button>
      <LastSyncLine lastRun={lastRun} />
      {notice && <p className="text-xs text-positive max-w-xs">{notice}</p>}
      {error && (
        <p className="text-xs text-negative max-w-xs">
          {error.text}
          {error.hint && (
            <>
              {' '}
              <Link to="/app/settings" className="text-accent-fg hover:underline">
                Go to Settings
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}

function LastSyncLine({ lastRun }: { lastRun: AmazonSyncRun | null }) {
  if (!lastRun) return null;
  if (lastRun.status === 'running') {
    return <p className="text-xs text-fg-tertiary">Last sync: syncing…</p>;
  }
  const finished = lastRun.finishedAt ? new Date(lastRun.finishedAt).toLocaleString() : 'unknown';
  return (
    <div>
      <p className="text-xs text-fg-tertiary">
        Last sync: {RUN_STATUS_LABEL[lastRun.status]} · {finished}
      </p>
      {lastRun.status === 'error' && lastRun.error && (
        <p className="text-xs text-negative max-w-xs">{lastRun.error}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Inventory density table
// ─────────────────────────────────────────────────────────

/** Shimmer placeholder for InventoryTable while the Amazon state query is
 *  loading — same grouped-header shape as the real table (toolbar omitted,
 *  since search/filter counts aren't known yet), 5 skeleton rows sized to
 *  the real InventoryRow columns. */
function InventoryTableSkeleton() {
  return (
    <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-x-auto">
      <table className="w-full text-sm min-w-[1050px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left">
            <th className="px-4 py-1.5 font-medium border-b border-border-subtle" />
            <th
              className="px-4 py-1.5 font-medium border-b border-border-subtle text-center"
              colSpan={3}
            >
              Inventory
            </th>
            <th className="px-4 py-1.5 font-medium border-b border-border-subtle text-center">
              Performance
            </th>
            <th className="px-4 py-1.5 font-medium border-b border-border-subtle text-center">
              Price &amp; fees
            </th>
            <th className="px-4 py-1.5 font-medium border-b border-border-subtle text-center">
              COGS
            </th>
          </tr>
          <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left">
            <th className="px-4 py-2 font-medium">Product</th>
            <th className="px-4 py-2 font-medium text-right">Fulfillable</th>
            <th className="px-4 py-2 font-medium text-right">Reserved</th>
            <th className="px-4 py-2 font-medium">Updated</th>
            <th className="px-4 py-2 font-medium text-center" />
            <th className="px-4 py-2 font-medium text-center" />
            <th className="px-4 py-2 font-medium text-center" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          <SkeletonRows
            rows={5}
            colWidths={['w-40', 'w-10', 'w-8', 'w-16', 'w-16', 'w-20', 'w-16']}
          />
        </tbody>
      </table>
    </div>
  );
}

export function InventoryTable({ rows }: { rows: AmazonInventoryRow[] }) {
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<AmazonStateFilterKey>(DEFAULT_AMAZON_STATE_FILTER);
  const [sortKey, setSortKey] = useState<AmazonSortKey>('fulfillable');
  const [sortDirection, setSortDirection] = useState<AmazonSortDirection>('desc');
  const [showOutOfStock, setShowOutOfStock] = useState(false);

  // Truly-dead listings (zero units anywhere at Amazon) are hidden by
  // default; the toggle brings them back. Items with inbound stock always
  // show — they're coming back to sellable.
  const outOfStockCount = useMemo(() => rows.filter(isOutOfStock).length, [rows]);
  const baseRows = useMemo(
    () => (showOutOfStock ? rows : rows.filter((r) => !isOutOfStock(r))),
    [rows, showOutOfStock]
  );

  const counts = useMemo(() => amazonStateFilterCounts(baseRows), [baseRows]);

  const visibleRows = useMemo(() => {
    const searched = searchAmazonInventory(baseRows, query);
    const filtered = filterAmazonInventoryByState(searched, stateFilter);
    return sortAmazonInventory(filtered, sortKey, sortDirection);
  }, [baseRows, query, stateFilter, sortKey, sortDirection]);

  function toggleSort(key: AmazonSortKey) {
    if (key === sortKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'asc' : 'desc');
    }
  }

  return (
    <div>
      {/* Toolbar */}
      <div
        className="flex items-center gap-2 flex-wrap"
        role="tablist"
        aria-label="Inventory state"
      >
        {AMAZON_STATE_FILTER_PILLS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={stateFilter === p.key}
            onClick={() => setStateFilter(p.key)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
              stateFilter === p.key
                ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
            )}
          >
            {p.label} · {counts[p.key]}
          </button>
        ))}
        {outOfStockCount > 0 && (
          <button
            type="button"
            aria-pressed={showOutOfStock}
            onClick={() => setShowOutOfStock((v) => !v)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
              showOutOfStock
                ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                : 'border-border-subtle text-fg-tertiary hover:text-fg-primary hover:border-border-default'
            )}
          >
            {showOutOfStock
              ? `Hide out of stock · ${outOfStockCount}`
              : `Show out of stock · ${outOfStockCount}`}
          </button>
        )}
      </div>
      <p className="text-xs text-fg-tertiary mt-1.5">
        Selling = has fulfillable stock right now. Products with zero units anywhere at Amazon are
        hidden until you show them. A green-tinted price pane means you hold the buy box.
      </p>

      <div className="mt-3 relative max-w-sm">
        <Search
          className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          aria-hidden="true"
        />
        <Input
          inputSize="sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search SKU, ASIN, or product name..."
          className="pl-9"
        />
      </div>

      {/* Table */}
      <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-x-auto">
        {visibleRows.length === 0 ? (
          <p className="text-sm text-fg-secondary px-5 py-6">No matches.</p>
        ) : (
          <table className="w-full text-sm min-w-[1050px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left">
                <th className="px-4 py-1.5 font-medium border-b border-border-subtle" />
                <th
                  className="px-4 py-1.5 font-medium border-b border-border-subtle text-center"
                  colSpan={3}
                >
                  Inventory
                </th>
                <th className="px-4 py-1.5 font-medium border-b border-border-subtle text-center">
                  Performance
                </th>
                <th className="px-4 py-1.5 font-medium border-b border-border-subtle text-center">
                  Price &amp; fees
                </th>
                <th className="px-4 py-1.5 font-medium border-b border-border-subtle text-center">
                  COGS
                </th>
              </tr>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left">
                <SortableTh
                  label="Product"
                  sortKey="name"
                  active={sortKey === 'name'}
                  direction={sortDirection}
                  onClick={() => toggleSort('name')}
                />
                <SortableTh
                  label="Fulfillable"
                  sortKey="fulfillable"
                  active={sortKey === 'fulfillable'}
                  direction={sortDirection}
                  onClick={() => toggleSort('fulfillable')}
                  align="right"
                />
                <SortableTh
                  label="Reserved"
                  sortKey="reserved"
                  active={sortKey === 'reserved'}
                  direction={sortDirection}
                  onClick={() => toggleSort('reserved')}
                  align="right"
                />
                <SortableTh
                  label="Updated"
                  sortKey="updatedAt"
                  active={sortKey === 'updatedAt'}
                  direction={sortDirection}
                  onClick={() => toggleSort('updatedAt')}
                />
                <th className="px-4 py-2 font-medium text-center" />
                <th className="px-4 py-2 font-medium text-center" />
                <SortableTh
                  label="COGS"
                  sortKey="cogs"
                  active={sortKey === 'cogs'}
                  direction={sortDirection}
                  onClick={() => toggleSort('cogs')}
                  align="right"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {visibleRows.map((row) => (
                <InventoryRow key={row.sellerSku} row={row} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SortableTh({
  label,
  active,
  direction,
  onClick,
  align = 'left',
}: {
  label: string;
  sortKey: AmazonSortKey;
  active: boolean;
  direction: AmazonSortDirection;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  const Icon = active ? (direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th className={cn('px-4 py-2 font-medium', align === 'right' && 'text-right')}>
      <button
        type="button"
        onClick={onClick}
        aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={cn(
          'inline-flex items-center gap-1 hover:text-fg-secondary transition-colors duration-fast',
          align === 'right' && 'flex-row-reverse'
        )}
      >
        {label}
        <Icon
          className={cn('h-3 w-3', active ? 'text-fg-secondary' : 'text-fg-tertiary')}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

export function InventoryRow({ row }: { row: AmazonInventoryRow }) {
  const label = row.productName ?? row.sellerSku;
  const updated = new Date(row.updatedAt).toLocaleDateString();

  return (
    <tr>
      {/* PRODUCT */}
      <td className="px-4 py-3 align-top">
        <div className="flex items-start gap-3 min-w-0">
          <ProductThumb imageUrl={row.imageUrl} alt={label} size="md" />
          <div className="min-w-0">
            <p className="text-sm text-fg-primary line-clamp-2" title={label}>
              <ProductLink asin={row.asin}>{label}</ProductLink>
            </p>
            <p className="text-xs font-mono text-fg-tertiary truncate">
              {row.asin ?? '—'} · {row.sellerSku}
            </p>
            {row.amazonRetail.status === 'amazon' && (
              <Badge variant="negative" title={amazonRetailReasonsTitle(row.amazonRetail.reasons)}>
                Amazon sells this
              </Badge>
            )}
          </div>
        </div>
      </td>

      {/* INVENTORY: fulfillable (big) + inbound sub-grid */}
      <td className="px-4 py-3 align-top text-right">
        <p className="font-display text-lg tabular-nums text-fg-primary">{row.fulfillable}</p>
        <p className="text-[10px] text-fg-tertiary mt-0.5 leading-relaxed">
          W {row.inboundWorking} · S {row.inboundShipped} · R {row.inboundReceiving}
        </p>
      </td>

      {/* INVENTORY: reserved */}
      <td className="px-4 py-3 align-top text-right tabular-nums">
        <Badge variant={row.reserved > 0 ? 'warning' : 'neutral'}>{row.reserved}</Badge>
      </td>

      {/* INVENTORY: updated */}
      <td className="px-4 py-3 align-top text-xs text-fg-tertiary whitespace-nowrap">{updated}</td>

      {/* PERFORMANCE — live sales velocity (Make Today real phase 2
          Instrument 3, 2026-09-01). unitsSold30d/7d null means velocity is
          unknown (no/insufficient recent sales), never a bare zero. */}
      <td className="px-4 py-3 align-top text-right">
        <p className="text-sm tabular-nums text-fg-primary">
          {performancePrimaryLabel(row.unitsSold30d)}
        </p>
        <p className="text-[10px] text-fg-tertiary mt-0.5">
          {performanceSecondaryLabel(row.unitsSold7d)}
        </p>
      </td>

      {/* PRICE & FEES — Your price anchors verdict/margin; Buy box is
          context only (words, not a dash, when there's none — same
          convention as PriceCalibrationRow.tsx). A held buy box tints the
          pane (same bg-positive treatment as PriceCalibrationRow's
          backdrop) so ownership scans at a glance. */}
      <td
        className={cn(
          'px-4 py-3 align-top text-right',
          row.buyboxIsYours === true && 'bg-positive/[0.06]'
        )}
      >
        <p className="text-sm font-semibold tabular-nums text-fg-primary">
          {money(row.yourPriceCents)}
        </p>
        <p className="text-[10px] text-fg-tertiary mt-0.5">{buyboxDisplayLabel(row)}</p>
        <p
          className="text-[10px] text-fg-tertiary mt-1 tabular-nums"
          title={
            row.feesCheckedAt ? `Fees checked ${feesCheckedAtLabel(row.feesCheckedAt)}` : undefined
          }
        >
          {feesDisplayLabel(row.feesAtPriceCents)}
          {row.feesCheckedAt && <span> · {feesCheckedAtLabel(row.feesCheckedAt)}</span>}
        </p>
      </td>

      {/* COGS (Faraz 2026-09-07: I need a column in OPS-Amazon for COGS;
          follow-up: prep cost too, $1.00 or $1.50 for glass). Primary =
          unit COGS (the one lib/finance/landed-cost.ts figure); secondary =
          the on-hand position valued at cost; the "accrued"/"est." markers +
          title flag the grade; the wrap toggle edits listing_map.
          prep_requires_wrap directly from the row. */}
      <td className="px-4 py-3 align-top text-right">
        <p className="text-sm tabular-nums text-fg-primary" title={cogsBreakdownTitle(row)}>
          {cogsPrimaryLabel(row.cogsUnitCents)}
          {row.cogsGrade === 'estimate' && (
            <span
              className="ml-1 text-[10px] font-normal text-fg-tertiary align-middle"
              title="Estimated from the most recent purchase order line for this vendor item — no received lot yet."
            >
              est.
            </span>
          )}
          {row.cogsGrade === 'accrued' && (
            <span
              className="ml-1 text-[10px] font-normal text-fg-tertiary align-middle"
              title="Some units' prep is accrued at the buying-settings rate — this service month's warehouse invoice hasn't landed yet."
            >
              accrued
            </span>
          )}
        </p>
        <p className="text-[10px] text-fg-tertiary mt-0.5">{cogsAtCostLabel(row)}</p>
        <div className="mt-1 flex justify-end">
          <WrapToggle
            sellerSku={row.sellerSku}
            checked={row.prepRequiresWrap}
            prepWrapSource={row.prepWrapSource}
          />
        </div>
      </td>
    </tr>
  );
}

/** COGS cell's "Glass / wrap" toggle — flips listing_map.prep_requires_wrap
 *  via PATCH /api/amazon/listings/:sellerSku and invalidates the inventory
 *  query so this row's COGS re-renders at the new prep rate. Owns its own
 *  mutation state, same pattern as PlanningPage's exclusion toggles.
 *
 *  Faraz 2026-09-08 glass-hint design: when no one has set the flag and the
 *  Amazon catalog attributes say glass (prepWrapSource === 'catalog'), the
 *  toggle shows the auto state ("Glass (auto)", tooltip names the catalog
 *  source) instead of the plain label — clicking it still sets the manual
 *  flag exactly as today.
 *
 *  2026-09-10 fix (Faraz: "It goes off and then comes back after a pause.
 *  User does not know what is happening"): `checked` is still driven by
 *  the ['amazon-state'] query, but useSetAmazonListingPrepWrap now patches
 *  that cache optimistically in onMutate, so this reflects the new value
 *  immediately instead of snapping back for the length of the refetch. */
function WrapToggle({
  sellerSku,
  checked,
  prepWrapSource,
}: {
  sellerSku: string;
  checked: boolean;
  prepWrapSource: AmazonInventoryRow['prepWrapSource'];
}) {
  const setPrepWrap = useSetAmazonListingPrepWrap();
  const isAuto = prepWrapSource === 'catalog';
  return (
    <div className="flex flex-col gap-0.5">
      <label
        className="inline-flex items-center gap-1 text-[10px] text-fg-tertiary cursor-pointer select-none"
        title={
          isAuto
            ? 'Auto-set from the Amazon catalog: this ASIN’s attributes indicate glass. Click to set manually.'
            : 'Prep at the wrapped rate ($1.50/unit today)'
        }
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={setPrepWrap.isPending}
          onChange={(e) => setPrepWrap.mutate({ sellerSku, prepRequiresWrap: e.target.checked })}
          className="h-3 w-3"
        />
        {isAuto ? 'Glass (auto)' : 'Glass / wrap'}
        {setPrepWrap.isPending && <span>Saving…</span>}
      </label>
      {setPrepWrap.isError && (
        <p className="text-negative">{(setPrepWrap.error as Error).message}</p>
      )}
    </div>
  );
}
